import { isAbortLikeError } from "@expert/cloudwatch-metrics";
import { useGaiaWebsocket } from "@expert/gaia";
import { useDebouncedCallback } from "@mantine/hooks";
import { CancelledError, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";

import type { SpellCheckResponse, SpellCheckStatus } from "@/sdk/agent/chat/types";

import { getRootDispatcher } from "@/analytics";
import { useLogger } from "@/sdk";
import { selectSpellCheck, useChatStore } from "@/sdk/agent/chat";

import type { UseSpellCheckOptions, UseSpellCheckResult } from "./types";

import { SPELLCHECK_CONFIG } from "./config";
import { gaiaSpellCheck } from "./gaiaSpellCheck";
import { getBackgroundSpellCheckMutationKey, getImmediateSpellCheckMutationKey } from "./types";

type CheckType = "background" | "immediate";
type MutationContext = { checkType: CheckType };

/**
 * Deferred promise used for coalescing: when an immediate check finds a background
 * run already in flight for the same content, it awaits this promise rather than
 * starting a duplicate GAIA request.
 */
interface Deferred<T> {
    promise: Promise<T>;
    resolve: (value: T | PromiseLike<T>) => void;
    reject: (reason?: unknown) => void;
}

function createDeferred<T>(): Deferred<T> {
    let resolve!: Deferred<T>["resolve"];
    let reject!: Deferred<T>["reject"];
    const promise = new Promise<T>((res, rej) => {
        resolve = res;
        reject = rej;
    });
    return { promise, resolve, reject };
}

export function useSpellCheck({
    chatId,
    sessionId,
    partner,
    enabled,
    config,
    localInput,
    sendMessage,
    spellCheckMetadataRef,
    onCancelWithText,
}: UseSpellCheckOptions): UseSpellCheckResult {
    const logger = useLogger({ module: "useSpellCheck", supportTeam: "messaging" });
    const queryClient = useQueryClient();

    const websocketObj = useGaiaWebsocket();
    const sendJsonMessage = websocketObj?.sendJsonMessage ?? (() => {});
    const websocketLoading = websocketObj?.loading ?? true;

    const updateSpellCheckStore = useChatStore((state) => state.updateSpellCheck);
    const clearSpellCheckStore = useChatStore((state) => state.clearSpellCheck);
    const spellCheckState = useChatStore(selectSpellCheck(chatId));

    const resolvedConfig = useMemo(() => ({ ...SPELLCHECK_CONFIG, ...config }), [config]);
    const chatAnalyticDispatcher = useMemo(() => getRootDispatcher().withIdentities({ ChatId: chatId }), [chatId]);

    const latestContentRef = useRef("");
    const backgroundAbortRef = useRef<AbortController | null>(null);
    /** Deferred used so an immediate coalesce can await the in-flight background. */
    const backgroundDeferredRef = useRef<Deferred<SpellCheckResponse | undefined> | null>(null);
    /** Stable ref to backgroundMutation.mutate so debouncedCheck doesn't re-create on every render. */
    const backgroundMutateRef = useRef<((content: string) => void) | null>(null);
    /** Stable ref to backgroundMutation.isPending so coalescing check doesn't re-create callbacks. */
    const backgroundIsPendingRef = useRef(false);
    /** Stable ref to immediateMutation.mutateAsync for same reason. */
    const immediateMutateAsyncRef = useRef<((content: string) => Promise<SpellCheckResponse | undefined>) | null>(null);

    useEffect(() => {
        spellCheckMetadataRef.current.spellcheckEnabled = enabled;
    }, [enabled, spellCheckMetadataRef]);

    const buildMutationHandlers = useCallback(
        (checkType: CheckType) => ({
            onMutate: (content: string): MutationContext => {
                logger.trace({ chatId, contentLength: content.length, checkType }, "Starting spellcheck");
                updateSpellCheckStore(chatId, {
                    status: "checking",
                    originalText: content,
                    showStatus: checkType === "immediate",
                });
                return { checkType };
            },

            onSuccess: (
                result: SpellCheckResponse | undefined,
                content: string,
                context: MutationContext | undefined,
            ) => {
                const resolvedCheckType = context?.checkType ?? checkType;
                const shouldShowStatus = resolvedCheckType === "immediate";

                if (checkType === "background") {
                    // Settle deferred so any coalesced immediate can continue
                    backgroundDeferredRef.current?.resolve(result);
                    backgroundDeferredRef.current = null;
                }

                if (latestContentRef.current !== content) {
                    logger.trace({ chatId }, "Spellcheck result discarded - content changed during check");
                    return;
                }

                if (!result) {
                    updateSpellCheckStore(chatId, {
                        status: "error",
                        errorMessage: "Spellcheck returned no result",
                        lastCheckedContent: content,
                        showStatus: shouldShowStatus,
                    });
                    void chatAnalyticDispatcher.dispatchBusinessEvent("ChatSpellCheckPerformed", {
                        chatId,
                        messageLength: content.length,
                        errorMessage: "Spellcheck returned no result",
                        checkType: resolvedCheckType,
                    });
                    return;
                }

                const newStatus: SpellCheckStatus = result.rewritingNeeded ? "suggestions" : "success";
                updateSpellCheckStore(chatId, {
                    status: newStatus,
                    result,
                    lastCheckedContent: content,
                    errorMessage: undefined,
                    showStatus: shouldShowStatus,
                });
                void chatAnalyticDispatcher.dispatchBusinessEvent("ChatSpellCheckPerformed", {
                    chatId,
                    messageLength: content.length,
                    rewritingNeeded: result.rewritingNeeded,
                    issuesFound: result.highlightSnippets?.length ?? 0,
                    checkType: resolvedCheckType,
                });
                logger.trace({ chatId, rewritingNeeded: result.rewritingNeeded }, "Spellcheck completed");
            },

            onError: (error: Error, content: string, context: MutationContext | undefined) => {
                const resolvedCheckType = context?.checkType ?? checkType;

                if (checkType === "background") {
                    backgroundDeferredRef.current?.reject(error);
                    backgroundDeferredRef.current = null;
                }

                if (error?.name === "AbortError" || error?.message?.includes("aborted")) {
                    logger.trace({ chatId }, "Spellcheck request cancelled (new request started)");
                    return;
                }
                const errorType = error?.constructor?.name ?? typeof error;
                const errorMessage = error instanceof Error ? error.message : String(error);
                logger.warn(
                    {
                        chatId,
                        errorType,
                        errorMessage,
                        contentLength: content.length,
                        checkType: resolvedCheckType,
                        error,
                    },
                    "Spellcheck failed",
                );
                updateSpellCheckStore(chatId, {
                    status: "error",
                    errorMessage: error instanceof Error ? error.message : "Spellcheck failed",
                    lastCheckedContent: content,
                    showStatus: resolvedCheckType === "immediate",
                });
                void chatAnalyticDispatcher.dispatchBusinessEvent("ChatSpellCheckPerformed", {
                    chatId,
                    messageLength: content.length,
                    errorMessage,
                    errorType,
                    checkType: resolvedCheckType,
                });
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [chatId, chatAnalyticDispatcher, logger, updateSpellCheckStore],
    );

    const backgroundHandlers = useMemo(() => buildMutationHandlers("background"), [buildMutationHandlers]);

    const backgroundMutation = useMutation<SpellCheckResponse | undefined, Error, string, MutationContext>({
        mutationKey: getBackgroundSpellCheckMutationKey(chatId),
        mutationFn: async (content: string) => {
            if (!sessionId || !chatId || !content.trim()) return undefined;
            // Supersede any prior background run
            backgroundAbortRef.current?.abort();
            const controller = new AbortController();
            backgroundAbortRef.current = controller;
            // Create deferred so checkSpellingImmediate can coalesce onto this request
            backgroundDeferredRef.current = createDeferred<SpellCheckResponse | undefined>();
            try {
                return await gaiaSpellCheck({ partner, sessionId, chatId, message: content }, sendJsonMessage, logger, {
                    timeoutMs: resolvedConfig.apiTimeoutMs,
                    signal: controller.signal,
                });
            } finally {
                if (backgroundAbortRef.current === controller) backgroundAbortRef.current = null;
            }
        },
        ...backgroundHandlers,
    });

    const immediateHandlers = useMemo(() => buildMutationHandlers("immediate"), [buildMutationHandlers]);

    const immediateMutation = useMutation<SpellCheckResponse | undefined, Error, string, MutationContext>({
        mutationKey: getImmediateSpellCheckMutationKey(chatId),
        mutationFn: async (content: string) => {
            if (!sessionId || !chatId || !content.trim()) return undefined;
            return gaiaSpellCheck({ partner, sessionId, chatId, message: content }, sendJsonMessage, logger, {
                timeoutMs: resolvedConfig.apiTimeoutMs,
            });
        },
        ...immediateHandlers,
    });

    // Keep stable refs in sync so debouncedCheck / checkSpellingImmediate don't re-create on every render
    backgroundMutateRef.current = backgroundMutation.mutate;
    backgroundIsPendingRef.current = backgroundMutation.isPending;
    immediateMutateAsyncRef.current = immediateMutation.mutateAsync;

    const abortBackgroundSpellCheck = useCallback(() => {
        void queryClient.cancelQueries({ queryKey: getBackgroundSpellCheckMutationKey(chatId) });
        backgroundAbortRef.current?.abort();
    }, [queryClient, chatId]);

    const debouncedCheck = useDebouncedCallback((content: string) => {
        const trimmed = content.trim();
        if (!enabled || !trimmed) {
            abortBackgroundSpellCheck();
            return;
        }
        if (trimmed.length < resolvedConfig.minCharsForBackgroundCheck) {
            logger.trace(
                { chatId, contentLength: trimmed.length, minChars: resolvedConfig.minCharsForBackgroundCheck },
                "Skipping background spellcheck - content too short",
            );
            abortBackgroundSpellCheck();
            return;
        }
        if (spellCheckState?.lastCheckedContent === content) return;
        if (!sessionId) {
            logger.warn({ chatId }, "Skipping spellcheck - sessionId not available");
            return;
        }
        if (websocketLoading) {
            logger.trace({ chatId }, "Skipping spellcheck - WebSocket not ready");
            return;
        }
        void queryClient.cancelQueries({ queryKey: getBackgroundSpellCheckMutationKey(chatId) });
        latestContentRef.current = content;
        backgroundMutateRef.current?.(content);
    }, resolvedConfig.debounceMs);

    // returned from hook, called by parent on every input change
    const triggerSpellCheck = useCallback(
        (content: string) => {
            if (!enabled) return;
            latestContentRef.current = content;
            const trimmed = content.trim();
            if (!trimmed || trimmed.length < resolvedConfig.minCharsForBackgroundCheck) {
                debouncedCheck.cancel();
                abortBackgroundSpellCheck();
                return;
            }
            abortBackgroundSpellCheck();
            debouncedCheck(content);
        },
        [enabled, debouncedCheck, resolvedConfig.minCharsForBackgroundCheck, abortBackgroundSpellCheck],
    );

    // used in handleSubmitMessage, captures reactive state so closure always sees current values
    const checkSpellingImmediate = useCallback(
        async (content: string): Promise<SpellCheckResponse | undefined> => {
            const trimmed = content.trim();
            if (!enabled || !trimmed) return undefined;
            if (!sessionId) {
                logger.warn({ chatId }, "Cannot run spellcheck - sessionId not available");
                return undefined;
            }
            if (websocketLoading) {
                logger.warn({ chatId }, "Cannot run spellcheck - WebSocket not ready");
                return undefined;
            }
            // Fast path: result already in store for this content
            if (
                spellCheckState?.lastCheckedContent === content &&
                spellCheckState?.result &&
                spellCheckState?.status !== "error"
            ) {
                return spellCheckState.result;
            }

            debouncedCheck.cancel();

            // Coalesce path: background already running for the same content — await it
            if (
                backgroundIsPendingRef.current &&
                latestContentRef.current === content &&
                backgroundDeferredRef.current
            ) {
                // Promote to immediate UX so status UI shows on send
                updateSpellCheckStore(chatId, { showStatus: true });
                return backgroundDeferredRef.current.promise;
            }

            // Stale background for different content — abort it and run immediate
            abortBackgroundSpellCheck();
            latestContentRef.current = content;
            const runImmediate = immediateMutateAsyncRef.current;
            if (!runImmediate) {
                logger.warn(
                    { chatId, contentLength: content.length },
                    "Spellcheck immediate mutation unavailable (ref unset) — skipping immediate check",
                );
                return undefined;
            }
            return runImmediate(content);
        },
        [
            enabled,
            sessionId,
            websocketLoading,
            chatId,
            spellCheckState,
            debouncedCheck,
            logger,
            updateSpellCheckStore,
            abortBackgroundSpellCheck,
        ],
    );

    // returned from hook, called by parent on message send to reset state
    const clearSpellCheck = useCallback(() => {
        debouncedCheck.cancel();
        abortBackgroundSpellCheck();
        clearSpellCheckStore(chatId);
    }, [chatId, clearSpellCheckStore, debouncedCheck, abortBackgroundSpellCheck]);

    // Abort background only on unmount — immediate is left to complete
    useEffect(() => {
        return () => {
            debouncedCheck.cancel();
            abortBackgroundSpellCheck();
        };
    }, [debouncedCheck, abortBackgroundSpellCheck]);

    const handleSubmitMessage = useCallback(async () => {
        // redundant check so we never send empty/whitespace-only messages
        if (!localInput.trim()) {
            return;
        }

        if (!enabled) {
            spellCheckMetadataRef.current = { spellcheckEnabled: false, spellcheckStatus: "disabled" };
            sendMessage(localInput);
            return;
        }

        const hasCheckedContent = spellCheckState?.lastCheckedContent === localInput;
        if (hasCheckedContent && spellCheckState?.result) {
            if (spellCheckState.result.rewritingNeeded) {
                updateSpellCheckStore(chatId, { status: "suggestions", showStatus: true });
                //TODO: if component unmounted we should show some indicator for background spellchecks that prevent message sending
                const payload = {
                    chatId,
                    messageLength: localInput.length,
                    issuesFound: spellCheckState.result.highlightSnippets?.length ?? 0,
                };
                void chatAnalyticDispatcher.dispatchBusinessEvent("ChatSpellCheckSuggestionsShown", payload);
                return;
            }
            updateSpellCheckStore(chatId, { status: "success", showStatus: true });
            spellCheckMetadataRef.current = { spellcheckEnabled: true, spellcheckStatus: "success" };
            sendMessage(localInput);
            return;
        }

        try {
            const result = await checkSpellingImmediate(localInput);
            if (!result) {
                updateSpellCheckStore(chatId, { status: "error", showStatus: true });
                spellCheckMetadataRef.current = { spellcheckEnabled: true, spellcheckStatus: "error" };
                sendMessage(localInput);
                return;
            }
            if (result.rewritingNeeded) {
                updateSpellCheckStore(chatId, {
                    status: "suggestions",
                    result,
                    lastCheckedContent: localInput,
                    originalText: localInput,
                    showStatus: true,
                });
                //TODO: if component unmounted we should show some indicator for background spellchecks that prevent message sending
                const payload = {
                    chatId,
                    messageLength: localInput.length,
                    issuesFound: result.highlightSnippets?.length ?? 0,
                };
                void chatAnalyticDispatcher.dispatchBusinessEvent("ChatSpellCheckSuggestionsShown", payload);
                return;
            }
            updateSpellCheckStore(chatId, { status: "success" });
            spellCheckMetadataRef.current = { spellcheckEnabled: true, spellcheckStatus: "success" };
            sendMessage(localInput);
        } catch (error) {
            if (isAbortLikeError(error) || error instanceof CancelledError) {
                logger.trace({ chatId }, "Spellcheck during send aborted or cancelled — not sending");
                return;
            }
            logger.warn({ error, chatId }, "Spellcheck failed during send");
            updateSpellCheckStore(chatId, { status: "error" });
            spellCheckMetadataRef.current = { spellcheckEnabled: true, spellcheckStatus: "error" };
            sendMessage(localInput);
        }
    }, [
        enabled,
        localInput,
        spellCheckState?.result,
        spellCheckState?.lastCheckedContent,
        checkSpellingImmediate,
        updateSpellCheckStore,
        sendMessage,
        chatId,
        chatAnalyticDispatcher,
        spellCheckMetadataRef,
        logger,
    ]);

    // passed as onSendWithChanges prop to SpellCheckStatus
    const handleSendWithChanges = useCallback(() => {
        if (!spellCheckState?.result?.rewrittenText) return;
        const correctedText = spellCheckState.result.rewrittenText;
        const originalText = spellCheckState.originalText ?? "";
        const issuesFixed = spellCheckState.result.highlightSnippets?.length ?? 0;

        const payload = {
            chatId,
            originalMessageLength: originalText.length,
            correctedMessageLength: correctedText.length,
            issuesFixed,
        };
        void chatAnalyticDispatcher.dispatchBusinessEvent("ChatSpellCheckSendWithChanges", payload);

        spellCheckMetadataRef.current = {
            spellcheckEnabled: true,
            spellcheckStatus: "suggestions_accepted",
            spellcheckIssuesFixed: issuesFixed,
            originalMessageLength: originalText.length,
        };
        updateSpellCheckStore(chatId, { status: "success" });
        sendMessage(correctedText);
    }, [
        spellCheckState?.result?.rewrittenText,
        spellCheckState?.originalText,
        spellCheckState?.result?.highlightSnippets,
        chatId,
        chatAnalyticDispatcher,
        updateSpellCheckStore,
        sendMessage,
        spellCheckMetadataRef,
    ]);

    // passed as onSendOriginal prop to SpellCheckStatus
    const handleSendOriginal = useCallback(() => {
        const originalText = spellCheckState?.originalText;
        if (!originalText) return;
        spellCheckMetadataRef.current = { spellcheckEnabled: true, spellcheckStatus: "skipped" };
        updateSpellCheckStore(chatId, { status: "success", showStatus: false });
        sendMessage(originalText);
    }, [spellCheckState?.originalText, chatId, updateSpellCheckStore, sendMessage, spellCheckMetadataRef]);

    // passed as onCancel prop to SpellCheckStatus, restores input text and hides status UI
    const handleCancelSpellCheck = useCallback(() => {
        if (!spellCheckState?.result?.rewrittenText) {
            updateSpellCheckStore(chatId, { showStatus: false });
            return;
        }
        const payload = {
            chatId,
            messageLength: spellCheckState.originalText?.length ?? spellCheckState.result.rewrittenText.length,
            issuesFound: spellCheckState.result.highlightSnippets?.length ?? 0,
        };
        void chatAnalyticDispatcher.dispatchBusinessEvent("ChatSpellCheckCancelled", payload);
        const textToRestore = resolvedConfig.replaceInputWithCorrectedOnCancel
            ? spellCheckState.result.rewrittenText
            : (spellCheckState.originalText ?? spellCheckState.result.rewrittenText);
        onCancelWithText(textToRestore);
        updateSpellCheckStore(chatId, { showStatus: false });
    }, [
        spellCheckState?.result?.rewrittenText,
        spellCheckState?.originalText,
        spellCheckState?.result?.highlightSnippets,
        chatId,
        chatAnalyticDispatcher,
        onCancelWithText,
        updateSpellCheckStore,
        resolvedConfig.replaceInputWithCorrectedOnCancel,
    ]);

    // passed as onDismiss prop to SpellCheckStatus
    const handleDismissStatus = useCallback(
        () => updateSpellCheckStore(chatId, { showStatus: false }),
        [chatId, updateSpellCheckStore],
    );

    const displayStatus = enabled && spellCheckState?.showStatus ? (spellCheckState?.status ?? "idle") : "idle";

    return {
        triggerSpellCheck,
        spellCheckState,
        clearSpellCheck,
        displayStatus,
        handleSubmitMessage,
        handleSendWithChanges,
        handleSendOriginal,
        handleCancelSpellCheck,
        handleDismissStatus,
    };
}
